/**
 * CashFlowAnalysisPage
 *
 * Página de análise profunda do fluxo de caixa.
 * Exibe histórico de saldo, gráficos, KPIs e insights.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeft, TrendingUp, Zap, BrainCircuit, Download,
  Target, Sparkles, Clock, PieChart, X,
  Calendar as CalendarIcon, RotateCcw
} from 'lucide-react'
import { CalendarRangePicker } from '../../../../components/ERP/Calendar/Calendar'
import { formatDateOnly, toDateInputValue } from '../../../../utils/dateUtils'
import { financeDashboardService } from '../../../../services/financeDashboardService'
import KPICard from '../../../../components/ERP/FinanceDashboard/KPICard/KPICard'
import DonutChart from '../../../../components/ERP/FinanceDashboard/DonutChart/DonutChart'
import BalanceHistoryChart from '../../../../components/ERP/FinanceDashboard/BalanceHistoryChart/BalanceHistoryChart'
import './CashFlowAnalysisPage.css'

function getDefaultDateRange() {
  const today = new Date()
  const start = new Date(today)
  start.setDate(start.getDate() - 29)
  return {
    from: toDateInputValue(start),
    to: toDateInputValue(today)
  }
}

function buildPeriodLabel(from, to) {
  if (!from || !to) return 'Selecionar período'
  return `${formatDateOnly(from)} — ${formatDateOnly(to)}`
}

function CashFlowAnalysisPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [initialLoading, setInitialLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [analysisData, setAnalysisData] = useState(null)
  const [error, setError] = useState(null)
  const [dateRange, setDateRange] = useState(getDefaultDateRange)
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const periodBtnRef = useRef(null)
  const [selectedCategoryId, setSelectedCategoryId] = useState(null)
  const hasDisplayedDataRef = useRef(false)

  const periodLabel = useMemo(
    () => buildPeriodLabel(dateRange.from, dateRange.to),
    [dateRange.from, dateRange.to]
  )

  const loadAnalysisData = useCallback(async () => {
    if (hasDisplayedDataRef.current) {
      setRefreshing(true)
    } else {
      setInitialLoading(true)
    }
    setError(null)

    try {
      const [year, month] = (dateRange.from || '').split('-').map(Number)
      const response = await financeDashboardService.getAnalysisData({
        month,
        year,
        dateFrom: dateRange.from,
        dateTo: dateRange.to,
        analyticsDateFrom: dateRange.from,
        analyticsDateTo: dateRange.to,
        historyOnly: true
      })
      if (response.success) {
        setAnalysisData(response.data)
        hasDisplayedDataRef.current = true
      } else {
        setError('Erro ao carregar dados de análise')
      }
    } catch (err) {
      console.error('Erro ao carregar dados de análise:', err)
      setError('Erro ao carregar dados. Tente novamente.')
    } finally {
      setInitialLoading(false)
      setRefreshing(false)
    }
  }, [dateRange.from, dateRange.to])

  useEffect(() => {
    loadAnalysisData()
  }, [loadAnalysisData])

  const handlePeriodRangeChange = (from, to) => {
    if (!from || !to) return
    setDateRange({ from, to })
  }

  const handleClearPeriodFilter = () => {
    setDateRange(getDefaultDateRange())
  }

  const opexCategories = useMemo(
    () => analysisData?.opexCategories || [],
    [analysisData?.opexCategories]
  )

  const allOpexSubCategories = useMemo(
    () => analysisData?.opexSubCategories || [],
    [analysisData?.opexSubCategories]
  )

  const selectedCategory = useMemo(
    () => opexCategories.find((cat) => String(cat.id) === String(selectedCategoryId)) || null,
    [opexCategories, selectedCategoryId]
  )

  const opexSubCategories = useMemo(() => {
    if (!selectedCategoryId) return allOpexSubCategories

    return allOpexSubCategories.filter(
      (item) =>
        String(item.parentId) === String(selectedCategoryId) ||
        String(item.id) === String(selectedCategoryId)
    )
  }, [allOpexSubCategories, selectedCategoryId])

  useEffect(() => {
    if (!analysisData || selectedCategoryId) return

    const initialSearch = location.state?.initialSearch
    if (!initialSearch) return

    const match = opexCategories.find(
      (cat) => cat.name?.toLowerCase() === String(initialSearch).toLowerCase()
    )
    if (match) {
      setSelectedCategoryId(match.id)
    }
  }, [analysisData, location.state, opexCategories, selectedCategoryId])

  const handleCategoryClick = (item) => {
    if (!item?.id) return
    setSelectedCategoryId(String(item.id) === String(selectedCategoryId) ? null : String(item.id))
  }

  const handleClearCategoryFilter = () => {
    setSelectedCategoryId(null)
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0
    }).format(value || 0)
  }

  const formatChange = (value, isPercentage = false) => {
    if (value === 0) return '0'
    const sign = value > 0 ? '+' : ''
    if (isPercentage) {
      return `${sign}${value.toFixed(1)}%`
    }
    if (Math.abs(value) >= 1000) {
      return `${sign}R$ ${(value / 1000).toFixed(1)}k`
    }
    return `${sign}R$ ${value.toFixed(0)}`
  }

  const strategicKPIs = analysisData?.strategicKPIs ? [
    {
      label: 'Burn Rate Diário',
      value: formatCurrency(Number(analysisData.strategicKPIs.dailyBurnRate) || 0),
      change: formatChange(Number(analysisData.strategicKPIs.dailyBurnRateChange) || 0, true),
      up: false,
      icon: Zap,
      color: 'text-amber-500',
      bg: 'bg-amber-50'
    },
    {
      label: 'Cash Runway (Fôlego)',
      value: `${Number(analysisData.strategicKPIs.cashRunwayDays) || 0} Dias`,
      change: `+${Number(analysisData.strategicKPIs.cashRunwayChange) || 0}d`,
      up: true,
      icon: Clock,
      color: 'text-blue-500',
      bg: 'bg-blue-50'
    },
    {
      label: 'Eficiência Operacional',
      value: `${(Number(analysisData.strategicKPIs.operationalEfficiency) || 0).toFixed(1)}%`,
      change: formatChange(Number(analysisData.strategicKPIs.operationalEfficiencyChange) || 0, true),
      up: true,
      icon: TrendingUp,
      color: 'text-emerald-500',
      bg: 'bg-emerald-50'
    },
    {
      label: 'Ponto de Equilíbrio',
      value: `Dia ${Number(analysisData.strategicKPIs.breakEvenDay) || 0}`,
      change: 'Estável',
      up: true,
      icon: Target,
      color: 'text-primary-500',
      bg: 'bg-primary-50'
    }
  ] : []

  if (initialLoading && !analysisData) {
    return (
      <div className="cash-flow-analysis-loading">
        <p>Carregando análise...</p>
      </div>
    )
  }

  if (error && !analysisData) {
    return (
      <div className="cash-flow-analysis-error">
        <p>{error}</p>
        <button type="button" onClick={loadAnalysisData}>Tentar novamente</button>
      </div>
    )
  }

  if (!analysisData) {
    return null
  }

  return (
    <div className={`cash-flow-analysis${refreshing ? ' is-refreshing' : ''}`}>
      <div className="cash-flow-analysis-header">
        <div className="cash-flow-analysis-header-left">
          <button
            type="button"
            onClick={() => navigate('/erp/financeiro/dashboard')}
            className="cash-flow-analysis-back-btn"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="cash-flow-analysis-title">
              Análise <span className="cash-flow-analysis-title-highlight">Profunda</span>
            </h1>
            <p className="cash-flow-analysis-subtitle">
              <BrainCircuit size={14} /> Inteligência Financeira Medik
            </p>
          </div>
        </div>
        <div className="cash-flow-analysis-header-actions">
          <button
            type="button"
            onClick={loadAnalysisData}
            className="cash-flow-analysis-reload-btn"
            disabled={refreshing}
            aria-label="Atualizar análise"
            title="Atualizar dados"
          >
            <RotateCcw
              size={18}
              className={refreshing ? 'cash-flow-analysis-reload-icon is-spinning' : 'cash-flow-analysis-reload-icon'}
            />
          </button>
          <button
            ref={periodBtnRef}
            type="button"
            onClick={() => setIsPeriodOpen((prev) => !prev)}
            className="cash-flow-analysis-period-btn"
            title="Filtrar período da análise"
          >
            <CalendarIcon size={14} />
            <span>{periodLabel}</span>
          </button>
          <CalendarRangePicker
            open={isPeriodOpen}
            onOpenChange={setIsPeriodOpen}
            anchorRef={periodBtnRef}
            startValue={dateRange.from}
            endValue={dateRange.to}
            onRangeChange={handlePeriodRangeChange}
            onClear={handleClearPeriodFilter}
            title="Período da análise"
            subtitle="Histórico de saldo e OPEX"
            maxDate={toDateInputValue(new Date())}
          />
          <button type="button" className="cash-flow-analysis-download-btn">
            <Download size={20} />
          </button>
        </div>
      </div>

      {error && (
        <div className="cash-flow-analysis-error-banner" role="alert">
          <p>{error}</p>
          <button type="button" onClick={loadAnalysisData}>Tentar novamente</button>
        </div>
      )}

      <div className="cash-flow-analysis-kpi-grid">
        {strategicKPIs.map((kpi, i) => (
          <KPICard
            key={i}
            label={kpi.label}
            value={kpi.value}
            change={kpi.change}
            up={kpi.up}
            icon={kpi.icon}
            color={kpi.color}
            bg={kpi.bg}
          />
        ))}
      </div>

      <BalanceHistoryChart
        balanceHistory={analysisData.balanceProjection}
        periodLabel={periodLabel}
      />

      <div className="cash-flow-analysis-donut-charts">
        <div className="cash-flow-analysis-donut-chart-card">
          <h3 className="cash-flow-analysis-donut-chart-title">
            <PieChart size={18} /> Distribuição de OPEX (Categorias)
          </h3>
          <p className="cash-flow-analysis-donut-chart-hint">
            Clique em uma categoria para detalhar as subcategorias
          </p>
          <DonutChart
            data={opexCategories}
            onItemClick={handleCategoryClick}
          />
        </div>
        <div className="cash-flow-analysis-donut-chart-card dark">
          <div className="cash-flow-analysis-donut-chart-header-row">
            <h3 className="cash-flow-analysis-donut-chart-title">
              <PieChart size={18} />
              {selectedCategory
                ? `Subcategorias — ${selectedCategory.name}`
                : 'Detalhar Fluxo de Caixa (Subcategorias)'}
            </h3>
            {selectedCategory && (
              <button
                type="button"
                className="cash-flow-analysis-donut-clear-btn"
                onClick={handleClearCategoryFilter}
                title="Limpar filtro de categoria"
              >
                <X size={14} />
                Ver todas
              </button>
            )}
          </div>
          <p className="cash-flow-analysis-donut-chart-hint">
            {selectedCategory
              ? 'Valores das subcategorias vinculadas à categoria selecionada'
              : 'Visão geral por subcategoria e categorias sem filhos'}
          </p>
          <DonutChart
            data={opexSubCategories}
            onItemClick={() => {}}
          />
        </div>
      </div>

      {analysisData?.geminiInsight && (
        <div className="cash-flow-analysis-insights">
          <div className="cash-flow-analysis-insight-card">
            <Sparkles size={20} className="cash-flow-analysis-insight-icon" />
            <div className="cash-flow-analysis-insight-content">
              <h4 className="cash-flow-analysis-insight-title">
                Insight Inteligente
              </h4>
              <p className="cash-flow-analysis-insight-text">
                {analysisData.geminiInsight.message}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CashFlowAnalysisPage
